import type { LabColor } from '../contracts/decors';
import type { Region } from '../contracts/decor-match';

/**
 * Извлекает усреднённый LAB-цвет региона из ImageData.
 *
 * **Type-уровневое требование P-009 / ADR-0009:** функция принимает только
 * `ImageData`, НЕ Blob/File/base64/URL. ImageData не имеет sensible
 * JSON-сериализации — не может случайно попасть в `JSON.stringify(body)`.
 *
 * Алгоритм:
 *   1. switch по `region.type` (exhaustive `assertNever` в default).
 *   2. Iterate пиксели:
 *      - 'average' — все пиксели ImageData
 *      - 'circle'  — пересечение круга с ImageData (partial overlap считаем,
 *                    не throw — за границей просто меньше пикселей в выборке)
 *   3. Alpha handling (per ADR-0009 §«Преимущества»): alpha === 0 пропускаются;
 *      иначе RGB обрабатывается как opaque (alpha не взвешиваем — фото с камеры
 *      всегда alpha=255, premultiplied averaging добавил бы сложность без
 *      пользы для основной аудитории).
 *   4. Среднее RGB → sRGB linearization → XYZ (D65) → LAB.
 *
 * Throws:
 *   - `imageData.width * imageData.height === 0`
 *   - `region.type === 'circle'` И `region.r ≤ 0` (страховка вне Zod-схемы)
 *   - 0 валидных пикселей после фильтрации (e.g. круг полностью вне фото
 *     ИЛИ все пиксели прозрачные)
 *
 * Pure: не мутирует ImageData, не использует сеть/глобальное состояние.
 */
export function extractLab(imageData: ImageData, region: Region): LabColor {
  const { width, height, data } = imageData;
  if (width * height === 0) {
    throw new Error('extractLab: imageData has zero area');
  }

  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let count = 0;

  switch (region.type) {
    case 'average': {
      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3]!;
        if (alpha === 0) continue;
        sumR += data[i]!;
        sumG += data[i + 1]!;
        sumB += data[i + 2]!;
        count++;
      }
      break;
    }
    case 'circle': {
      const { cx, cy, r } = region;
      if (r <= 0) {
        throw new Error('extractLab: region.r must be > 0');
      }
      const r2 = r * r;
      // Bounding box clamped к ImageData. Partial overlap = меньше пикселей
      // в цикле, но не throw (per Plan-agent: defensive vs. mismatched coords).
      const xMin = Math.max(0, Math.floor(cx - r));
      const xMax = Math.min(width - 1, Math.ceil(cx + r));
      const yMin = Math.max(0, Math.floor(cy - r));
      const yMax = Math.min(height - 1, Math.ceil(cy + r));
      for (let y = yMin; y <= yMax; y++) {
        for (let x = xMin; x <= xMax; x++) {
          // Pixel center (x+0.5, y+0.5) vs circle center.
          const dx = x + 0.5 - cx;
          const dy = y + 0.5 - cy;
          if (dx * dx + dy * dy > r2) continue;
          const idx = (y * width + x) * 4;
          const alpha = data[idx + 3]!;
          if (alpha === 0) continue;
          sumR += data[idx]!;
          sumG += data[idx + 1]!;
          sumB += data[idx + 2]!;
          count++;
        }
      }
      break;
    }
    default:
      assertNever(region);
  }

  if (count === 0) {
    throw new Error('extractLab: no opaque pixels in selected region');
  }

  return rgbToLab(sumR / count, sumG / count, sumB / count);
}

function assertNever(x: never): never {
  throw new Error(`extractLab: unexpected region type: ${JSON.stringify(x)}`);
}

// ── sRGB → XYZ (D65) → CIE Lab ────────────────────────────────────────────
// Стандартные коэффициенты sRGB ↔ XYZ D65, формула CIE Lab.
// Reference: https://en.wikipedia.org/wiki/SRGB,
//            https://en.wikipedia.org/wiki/CIELAB_color_space

function srgbToLinear(channel0to255: number): number {
  const s = channel0to255 / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

const Xn = 0.95047;
const Yn = 1.0;
const Zn = 1.08883;
const DELTA = 6 / 29;
const DELTA3 = DELTA * DELTA * DELTA;

function fLab(t: number): number {
  return t > DELTA3 ? Math.cbrt(t) : t / (3 * DELTA * DELTA) + 4 / 29;
}

function rgbToLab(r: number, g: number, b: number): LabColor {
  const rl = srgbToLinear(r);
  const gl = srgbToLinear(g);
  const bl = srgbToLinear(b);

  const X = 0.4124564 * rl + 0.3575761 * gl + 0.1804375 * bl;
  const Y = 0.2126729 * rl + 0.7151522 * gl + 0.072175 * bl;
  const Z = 0.0193339 * rl + 0.119192 * gl + 0.9503041 * bl;

  const fx = fLab(X / Xn);
  const fy = fLab(Y / Yn);
  const fz = fLab(Z / Zn);

  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
}
